<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="tw-fixed tw-inset-0 tw-z-50 tw-overflow-y-auto"
      @keydown.esc="handleClose"
    >
      <div class="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-p-4">
        <div
          class="tw-fixed tw-inset-0 tw-bg-slate-900 tw-bg-opacity-60 tw-transition-opacity"
          @click="handleClose"
        />

        <div class="tw-relative tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-max-w-2xl tw-w-full tw-p-6 md:tw-p-8">
          <div class="tw-flex tw-items-start tw-justify-between tw-gap-4">
            <div>
              <h3 class="tw-text-lg tw-font-semibold tw-text-slate-900">
                Request amendments
              </h3>
              <p class="tw-mt-1 tw-text-sm tw-text-slate-600">
                Let us know what needs changing, We will get back to you when we've updated your quote.
              </p>
            </div>

            <button
              type="button"
              class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-slate-100 tw-text-slate-600 tw-h-9 tw-w-9 hover:tw-bg-slate-200"
              aria-label="Close amendments modal"
              @click="handleClose"
            >
              <span class="tw-text-lg">×</span>
            </button>
          </div>

          <!-- Requester details -->
          <div class="tw-mt-5 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
            <div>
              <label class="tw-block tw-text-xs tw-font-medium tw-text-slate-600 tw-uppercase tw-tracking-wide">
                Your name
              </label>
              <input
                :value="requesterName"
                type="text"
                class="tw-mt-1 tw-block tw-w-full tw-rounded-md tw-border tw-border-slate-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
                placeholder="e.g. John Smith"
                required
                @input="$emit('update:requesterName', $event.target.value)"
              >
            </div>
            <div>
              <label class="tw-block tw-text-xs tw-font-medium tw-text-slate-600 tw-uppercase tw-tracking-wide">
                Your email
              </label>
              <input
                :value="requesterEmail"
                type="email"
                class="tw-mt-1 tw-block tw-w-full tw-rounded-md tw-border tw-border-slate-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
                placeholder="you@example.com"
                required
                @input="$emit('update:requesterEmail', $event.target.value)"
              >
            </div>
          </div>

          <div class="tw-mt-6 tw-space-y-4">
            <!-- Amend all items -->
            <div class="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-4">
              <label class="tw-flex tw-items-start tw-gap-3 tw-cursor-pointer">
                <input
                  :checked="amendAllSelected"
                  type="checkbox"
                  class="tw-mt-1 tw-h-4 tw-w-4 tw-text-blue-600 tw-border-slate-300 tw-rounded focus:tw-ring-blue-500"
                  @change="$emit('update:amendAllSelected', $event.target.checked)"
                >
                <div>
                  <p class="tw-text-sm tw-font-medium tw-text-slate-900">
                    Apply the same change to all items
                  </p>
                  <p class="tw-mt-0.5 tw-text-xs tw-text-slate-500">
                    Got one change needed across the whole quote? Describe it here...
                  </p>
                </div>
              </label>
              <div class="tw-mt-3">
                <textarea
                  :value="amendAllNotes"
                  rows="3"
                  class="tw-w-full tw-rounded-md tw-border tw-border-slate-300 tw-p-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
                  placeholder="Describe the change that should apply to all items…"
                  :disabled="!amendAllSelected"
                  @input="$emit('update:amendAllNotes', $event.target.value)"
                />
              </div>
            </div>

            <div class="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4">
              <p class="tw-text-xs tw-font-medium tw-tracking-wide tw-text-slate-600 tw-uppercase">
                Items
              </p>

              <div class="tw-mt-3 tw-space-y-3">
                <div
                  v-for="amendment in amendmentOptions"
                  :key="amendment.key"
                  class="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-4"
                >
                  <div class="tw-flex tw-items-start tw-gap-3">
                    <div class="tw-flex-1">
                      <div class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-start sm:tw-justify-between tw-gap-2">
                        <div>
                          <p class="tw-text-sm tw-font-medium tw-text-slate-900">
                            {{ amendment.title }}
                          </p>
                          <p
                            v-if="amendment.subtitle"
                            class="tw-mt-0.5 tw-text-xs tw-text-slate-500"
                          >
                            {{ amendment.subtitle }}
                          </p>
                        </div>

                        <div
                          v-if="amendment.type === 'item'"
                          class="tw-text-xs tw-text-slate-600 sm:tw-text-right"
                        >
                          <div class="tw-font-medium tw-text-slate-800">
                            {{ formatCurrency(amendment.lineTotal) }}
                          </div>
                          <div class="tw-text-slate-500">
                            {{ formatCurrency(amendment.unitPrice) }} each
                          </div>
                          <div
                            v-if="autoDeltaTotal(amendment) !== 0"
                            class="tw-mt-1 tw-text-[11px] tw-text-slate-500"
                          >
                            After selected options: {{ formatCurrency(lineTotalWithAuto(amendment)) }}
                          </div>
                        </div>
                      </div>

                      <div
                        v-if="amendment.type === 'item' && (amendment.image || amendment.descriptionHtml)"
                        class="tw-mt-3 tw-flex tw-gap-3"
                      >
                        <div
                          v-if="amendment.image"
                          class="tw-h-24 tw-w-24 tw-rounded-lg tw-overflow-hidden tw-bg-white tw-border tw-border-slate-200 tw-flex-shrink-0 tw-p-1"
                        >
                          <img
                            :src="amendment.image"
                            :alt="amendment.title"
                            class="tw-h-full tw-w-full tw-object-contain"
                          >
                        </div>

                        <div
                          v-if="amendment.descriptionHtml"
                          class="tw-flex-1"
                        >
                          <div
                            class="tw-text-xs tw-text-slate-600 tw-leading-relaxed"
                            :class="amendment.descriptionExpanded ? '' : 'tw-max-h-20 tw-overflow-hidden tw-relative'"
                          >
                            <div
                              class="tw-prose tw-prose-sm tw-max-w-none tw-prose-slate tw-text-xs"
                              v-html="amendment.descriptionHtml"
                            />
                            <div
                              v-if="!amendment.descriptionExpanded"
                              class="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-10 tw-bg-gradient-to-t tw-from-white tw-to-white/0"
                            />
                          </div>

                          <button
                            type="button"
                            class="tw-mt-2 tw-text-xs tw-font-medium tw-text-blue-600 hover:tw-underline"
                            @click.stop.prevent="toggleDescription(amendment)"
                          >
                            {{ amendment.descriptionExpanded ? 'Hide details' : 'Show details' }}
                          </button>
                        </div>
                      </div>

                      <div
                        v-if="amendment.type === 'item' && amendment.autoAmendments && amendment.autoAmendments.length"
                        class="tw-mt-4 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-3"
                      >
                        <p class="tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-600">
                          Changes
                        </p>

                        <div class="tw-mt-2 tw-space-y-2">
                          <div
                            v-for="opt in amendment.autoAmendments"
                            :key="opt.key"
                            class="tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-p-3"
                          >
                            <label class="tw-flex tw-items-start tw-gap-3 tw-cursor-pointer">
                              <input
                                v-model="opt.selected"
                                type="checkbox"
                                class="tw-mt-1 tw-h-4 tw-w-4 tw-text-blue-600 tw-border-slate-300 tw-rounded focus:tw-ring-blue-500"
                              >

                              <div class="tw-flex-1">
                                <div class="tw-flex tw-items-start tw-justify-between tw-gap-3">
                                  <div>
                                    <p class="tw-text-sm tw-font-medium tw-text-slate-900">
                                      {{ opt.title }}
                                    </p>
                                    <p
                                      v-if="opt.subtitle"
                                      class="tw-mt-0.5 tw-text-xs tw-text-slate-500"
                                    >
                                      {{ opt.subtitle }}
                                    </p>
                                  </div>

                                  <div
                                    v-if="(parseFloat(opt.priceDelta) || 0) > 0"
                                    class="tw-text-xs tw-text-slate-700 tw-font-medium"
                                  >
                                    +{{ formatCurrency(opt.priceDelta) }}
                                  </div>
                                </div>

                                <div
                                  v-if="opt.selected"
                                  class="tw-mt-2 tw-space-y-2"
                                >
                                  <input
                                    v-if="opt.code === 'CUSTOM'"
                                    v-model="opt.customTitle"
                                    type="text"
                                    class="tw-w-full tw-rounded-md tw-border tw-border-slate-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
                                    placeholder="What change do you want? (e.g. Add trickle vents)"
                                  >

                                  <textarea
                                    v-model="opt.notes"
                                    rows="2"
                                    class="tw-w-full tw-rounded-md tw-border tw-border-slate-300 tw-p-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
                                    :placeholder="opt.placeholder"
                                  />
                                </div>
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="tw-flex tw-flex-col-reverse sm:tw-flex-row sm:tw-justify-end tw-gap-3">
              <button
                type="button"
                class="tw-inline-flex tw-justify-center tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 hover:tw-bg-slate-50"
                :disabled="submitting"
                @click="handleClose"
              >
                Cancel
              </button>
              <button
                type="button"
                class="tw-inline-flex tw-justify-center tw-rounded-md tw-border tw-border-transparent tw-bg-blue-600 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-shadow-sm hover:tw-bg-blue-700 disabled:tw-opacity-60"
                :disabled="submitting"
                @click="$emit('submit')"
              >
                <span v-if="submitting">Submitting…</span>
                <span v-else>Submit request</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
export default {
  name: 'AmendmentsModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    submitting: {
      type: Boolean,
      default: false
    },
    amendmentOptions: {
      type: Array,
      default: () => []
    },
    amendAllSelected: {
      type: Boolean,
      default: false
    },
    amendAllNotes: {
      type: String,
      default: ''
    },
    requesterName: {
      type: String,
      default: ''
    },
    requesterEmail: {
      type: String,
      default: ''
    }
  },
  emits: [
    'close',
    'submit',
    'update:amendAllSelected',
    'update:amendAllNotes',
    'update:requesterName',
    'update:requesterEmail'
  ],
  setup(props, { emit }) {
    const handleClose = () => {
      if (props.submitting) return
      emit('close')
    }

    const toggleDescription = (amendment) => {
      amendment.descriptionExpanded = !amendment.descriptionExpanded
    }

    const formatCurrency = (amount) => {
      const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP'
      }).format(numericAmount || 0)
    }

    const autoDeltaTotal = (amendment) => {
      const opts = amendment?.autoAmendments || []
      return opts.reduce((sum, opt) => {
        if (!opt?.selected) return sum
        return sum + (parseFloat(opt.priceDelta) || 0)
      }, 0)
    }

    const lineTotalWithAuto = (amendment) => {
      return (parseFloat(amendment?.lineTotal) || 0) + autoDeltaTotal(amendment)
    }

    return {
      handleClose,
      toggleDescription,
      formatCurrency,
      autoDeltaTotal,
      lineTotalWithAuto
    }
  }
}
</script>
